<script setup lang="ts">
import Toast from 'primevue/toast'
import ConfirmDialog from 'primevue/confirmdialog'
import { RouterView, useRoute } from 'vue-router'
import { onMounted, ref, watch } from 'vue'
import { useAuthStore } from '@/stores/authStore'
import { useMaintenanceStore } from '@/stores/maintenanceStore'
import { useChatStore } from '@/stores/chatStore'
import { chatInitializationService } from '@/services/chatInitializationService'
import CookieConsentBanner from '@/components/common/CookieConsentBanner.vue'
import OneSignalRegistration from '@/components/common/OneSignalRegistration.vue'
import MaintenancePage from '@/components/MaintenancePage.vue'

const authStore = useAuthStore()
const maintenanceStore = useMaintenanceStore()
const chatStore = useChatStore()
const isInitialized = ref(false)
const route = useRoute()

onMounted(async () => {
  try {
    // Check maintenance mode first
    await maintenanceStore.checkMaintenanceMode()
    
    // If not in maintenance mode, initialize auth
    if (!maintenanceStore.isMaintenanceMode) {
      await authStore.initializeAuth()
      
      // Initialize chat system if user is authenticated
      if (authStore.isLoggedIn) {
        try {
          await chatInitializationService.initialize()
        } catch (error) {
          console.warn('Chat system initialization failed:', error)
          // Don't throw - app should still work without chat
        }
      }
    }
  } catch (error) {
    console.error('Error during app initialization:', error)
  } finally {
    isInitialized.value = true
  }
})

// Watch for authentication changes and initialize/cleanup chat accordingly
watch(
  () => authStore.isLoggedIn,
  async (isLoggedIn, wasLoggedIn) => {
    if (isLoggedIn && !wasLoggedIn) {
      // User just logged in - initialize chat
      try {
        await chatInitializationService.initialize()
      } catch (error) {
        console.warn('Chat system initialization failed after login:', error)
      }
    } else if (!isLoggedIn && wasLoggedIn) {
      // User just logged out - cleanup chat
      chatStore.disconnect()
      chatInitializationService.reset()
    }
  },
  { immediate: false }
)

// Watch for route, user role and the Strapi setting to toggle accessibility widget
watch(
  [
    () => route.path,
    () => authStore.user?.role?.name,
    () => maintenanceStore.isAccessibilityWidgetEnabled,
  ],
  ([path, roleName, isWidgetEnabled]) => {
    // Hide accessibility widget if in Admin View (dashboard) OR user is not Organization
    // "Hidden in Admin view or when the user does not have the 'Organization' role."
    const isAdminView = path.startsWith('/dashboard')
    const isOrganization = roleName === 'Organization'

    // Logic: Hide if (Admin) OR (Not Organization)
    // Wait, usually Organization role is the one managing their profile.
    // If a regular user visits the site, should they see it?
    // "when the user does not have the 'Organization' role" implies only Organizations see it?
    // This seems specific to an editor tool perhaps? Or maybe the user phrasing is tricky.
    // "Hidden ... when the user does not have the 'Organization' role" means:
    // IF (User != Organization) THEN Hidden.
    // So ONLY Organization users see it?
    // AND it must NOT be Admin view?

    // Let's implement exactly as requested:
    // Hidden condition: (isAdminView) || (role !== 'Organization')

    // The `accessibility_widget` setting in Strapi switches it off everywhere
    const shouldHide = !isWidgetEnabled || isAdminView || !isOrganization

    if (shouldHide) {
      document.body.classList.add('hide-accessibility-widget')
    } else {
      document.body.classList.remove('hide-accessibility-widget')
    }
  },
  { immediate: true }
)
</script>

<style>
/* Global style to hide accessibility widget when class is present */
body.hide-accessibility-widget #aioa-adawidget-icon,
body.hide-accessibility-widget .aioa-icon,
body.hide-accessibility-widget img[alt="Accessibility Widget"],
body.hide-accessibility-widget #accessibility_settings_toggle {
  display: none !important;
}
</style>

<template>
  <Toast />
  <ConfirmDialog />
  
  <!-- Show maintenance page if maintenance mode is active -->
  <MaintenancePage v-if="isInitialized && maintenanceStore.isMaintenanceMode" />
  
  <!-- Show normal app if not in maintenance mode -->
  <template v-else-if="isInitialized && !maintenanceStore.isMaintenanceMode">
    <RouterView />
    <CookieConsentBanner />
    <OneSignalRegistration />
  </template>
  
  <!-- Loading state -->
  <div v-else class="min-h-screen bg-gradient-to-br from-[#00B6A3] to-[#23036A] flex items-center justify-center">
    <div class="text-white text-center">
      <div class="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p>{{ $t('common.loading', 'Loading...') }}</p>
    </div>
  </div>
</template>
